import React, { useState, useEffect } from "react";
import {
  MapPin, Shield, Thermometer, Zap, Clock, Globe,
  CheckCircle, AlertTriangle, Smartphone, BookOpen, ChevronRight
} from "lucide-react";
import { EmergencyBlock } from "../EmergencyBlock";
import { ScamRadarSection } from "../scamradar/ScamRadarSection";

// ── Helpers ─────────────────────────────────────────────────────────────────────
function weatherEmoji(desc = "") {
  const d = desc.toLowerCase();
  if (d.includes("neige") || d.includes("snow")) return "❄️";
  if (d.includes("orage") || d.includes("storm")) return "⛈️";
  if (d.includes("pluie") || d.includes("rain")) return "🌧️";
  if (d.includes("nuage") || d.includes("couvert")) return "⛅";
  if (d.includes("caniculaire") || d.includes("très chaud")) return "🌡️";
  if (d.includes("chaud") || d.includes("soleil") || d.includes("ensoleillé")) return "☀️";
  if (d.includes("doux") || d.includes("agréable")) return "🌤️";
  return "🌡️";
}

// ── Local Time Clock ────────────────────────────────────────────────────────────
function LocalClock({ timezoneIana, decalageHoraire, destination }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const parisTimeStr = now.toLocaleTimeString("fr-FR", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/Paris",
  });

  let destTimeStr = null;
  let offsetLabel = null;

  if (timezoneIana) {
    try {
      destTimeStr = now.toLocaleTimeString("fr-FR", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: timezoneIana,
      });
      const destHour = parseInt(new Date().toLocaleTimeString("en-US", { hour: "2-digit", hour12: false, timeZone: timezoneIana }));
      const parisHour = parseInt(new Date().toLocaleTimeString("en-US", { hour: "2-digit", hour12: false, timeZone: "Europe/Paris" }));
      let diff = destHour - parisHour;
      if (diff > 12) diff -= 24;
      if (diff < -12) diff += 24;
      offsetLabel = diff !== 0 ? `${diff > 0 ? "+" : ""}${diff}h vs Paris` : "Même heure que Paris";
    } catch {
      destTimeStr = null;
    }
  }

  if (!destTimeStr && decalageHoraire) {
    const match = decalageHoraire.match(/([\+\-]?\d+)\s*h(?:\s*(\d+))?/i);
    if (match) {
      const hoursOff = parseInt(match[1]);
      const minsOff = match[2] ? parseInt(match[2]) : 0;
      const totalMinOff = hoursOff * 60 + (hoursOff >= 0 ? minsOff : -minsOff);
      const parisMs = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Paris" })).getTime();
      const destDate = new Date(parisMs + totalMinOff * 60000);
      destTimeStr = destDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      offsetLabel = `${hoursOff > 0 ? "+" : ""}${hoursOff}h vs Paris`;
    }
  }

  if (!destTimeStr) return null;

  return (
    <div className="card-surface border-l-4 border-[#C17F59]">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={14} strokeWidth={1.5} className="text-[#C17F59]" />
        <span className="text-sm font-medium text-[#2B2623]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>
          Heure locale — {destination}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#8B5E34] mb-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>{destination}</p>
          <p className="text-3xl text-[#C17F59]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {destTimeStr}
          </p>
          {offsetLabel && (
            <p className="text-xs text-[#9A8F86] mt-1 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>{offsetLabel}</p>
          )}
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-[#8B5E34] mb-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>Paris</p>
          <p className="text-3xl text-[#5C554E]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>
            {parisTimeStr}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Emergency Numbers ───────────────────────────────────────────────────────────
// Extracts a dialable phone number from a string like "Police: 17" or "+212 19" or "15 (SAMU)"
function extractDialable(text = "") {
  const match = text.match(/[\+\d][\d\s\-\.\(\)]{3,}/);
  if (!match) return null;
  const digits = match[0].replace(/[\s\-\.\(\)]/g, "");
  return digits.length >= 3 ? digits : null;
}

// ── Security Section ────────────────────────────────────────────────────────────
function SecuritySection({ securite }) {
  if (!securite) return null;
  const riskColors = {
    "Faible": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Modéré": "bg-amber-50 text-amber-700 border-amber-200",
    "Élevé": "bg-orange-50 text-orange-700 border-orange-200",
    "Très élevé": "bg-red-50 text-red-700 border-red-200",
  };
  const riskStyle = riskColors[securite.niveau_risque] || riskColors["Modéré"];

  return (
    <div className="card-surface space-y-5" data-testid="security-section">
      <div className="flex items-center justify-between">
        <h3 className="text-xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
          Sécurité & arnaques locales
        </h3>
        {securite.niveau_risque && (
          <span className={`text-xs font-medium px-3 py-1 border rounded-sm ${riskStyle}`} style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Risque : {securite.niveau_risque}
          </span>
        )}
      </div>
      {securite.arnaques_courantes?.length > 0 && (
        <div>
          <p className="section-label mb-3">Arnaques connues</p>
          <div className="space-y-3">
            {securite.arnaques_courantes.map((arnaque, i) => (
              <div key={i} className="bg-amber-50 border border-amber-200 p-3.5 rounded-sm" data-testid={`scam-item-${i}`}>
                <p className="text-sm font-medium text-amber-900 mb-1 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontSize: "1rem" }}>
                  <AlertTriangle size={13} strokeWidth={1.5} className="flex-shrink-0" /> {arnaque.titre}
                </p>
                <p className="text-sm text-amber-800 mb-2 leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>{arnaque.description}</p>
                <div className="flex items-start gap-2 bg-white/60 p-2 rounded-sm">
                  <CheckCircle size={12} strokeWidth={1.5} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-emerald-800 leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>{arnaque.conseil}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {securite.comportements_dangereux?.length > 0 && (
        <div>
          <p className="section-label mb-2">Situations à éviter</p>
          <ul className="space-y-1.5">
            {securite.comportements_dangereux.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <span className="text-orange-500 flex-shrink-0 mt-0.5">⚠</span> {c}
              </li>
            ))}
          </ul>
        </div>
      )}
      {securite.conseils_essentiels?.length > 0 && (
        <div>
          <p className="section-label mb-2">Conseils essentiels</p>
          <ul className="space-y-1.5">
            {securite.conseils_essentiels.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <ChevronRight size={12} strokeWidth={2} className="text-[#C17F59] mt-0.5 flex-shrink-0" /> {c}
              </li>
            ))}
          </ul>
        </div>
      )}
      {securite.zones_a_eviter?.length > 0 && (
        <div>
          <p className="section-label mb-2">Zones à éviter</p>
          <ul className="space-y-1.5">
            {securite.zones_a_eviter.map((z, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-sm" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <span className="flex-shrink-0">🚫</span> {z}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Offline Map Card ────────────────────────────────────────────────────────────
function OfflineMapCard({ carteHorsConnexion }) {
  const defaultApps = [
    { nom: "Maps.me", usage: "Cartes vectorielles gratuites téléchargeables, 100% hors connexion", plateforme: "iOS / Android", gratuit: true },
    { nom: "OsmAnd", usage: "Cartes OpenStreetMap pour randonnée et zones reculées", plateforme: "iOS / Android", gratuit: true },
    { nom: "Google Maps", usage: "Télécharger une zone avant le départ : Enregistrer → Zone hors connexion", plateforme: "iOS / Android", gratuit: true },
  ];
  const apps = carteHorsConnexion?.apps_recommandees || defaultApps;
  const conseil = carteHorsConnexion?.conseil_specifique;

  return (
    <div className="card-surface" data-testid="offline-map-card">
      <h3 className="text-xl mb-3 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
        <Globe size={15} strokeWidth={1.5} className="text-[#C17F59]" /> Cartes hors connexion
      </h3>
      {conseil && (
        <div className="bg-[#FDF8F3] border-l-2 border-[#C17F59] p-3 mb-4 rounded-sm">
          <p className="text-sm text-[#5C554E] italic leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>{conseil}</p>
        </div>
      )}
      <div className="space-y-2">
        {apps.map((app, i) => (
          <div key={i} className="flex items-start gap-3 bg-[#F9F6F0] border border-[#DCD4C6] p-3 rounded-sm">
            <div className="w-9 h-9 bg-[#C17F59] flex items-center justify-center text-[#F9F6F0] text-sm font-bold flex-shrink-0 rounded-sm">
              {app.nom[0]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-[#2B2623]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>{app.nom}</span>
                <span className="text-xs text-[#9A8F86]" style={{ fontFamily: "'Lora', Georgia, serif" }}>{app.plateforme}</span>
                {app.gratuit && <span className="text-xs text-emerald-600 border border-emerald-200 px-1.5 py-0.5 rounded-sm bg-emerald-50">Gratuit</span>}
              </div>
              <p className="text-xs text-[#5C554E] mt-0.5 leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>{app.usage}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main PracticalInfoTab ───────────────────────────────────────────────────────
export function PracticalInfoTab({ result, destination, tripId, planType, isAdmin }) {
  const ip = result?.infos_pratiques;

  if (!ip) return (
    <div className="text-[#9A8F86] py-10 text-center italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
      Infos pratiques non disponibles
    </div>
  );

  const InfoRow = ({ icon, label, value }) => value ? (
    <div className="flex items-start gap-3 py-3 border-b border-[#DCD4C6] last:border-0">
      <div className="text-[#C17F59] mt-0.5 flex-shrink-0">{icon}</div>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#8B5E34] mb-0.5" style={{ fontFamily: "'Lora', Georgia, serif" }}>{label}</p>
        <p className="text-sm text-[#5C554E] leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>{value}</p>
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-5">
      <LocalClock timezoneIana={ip.timezone_iana || null} decalageHoraire={ip.decalage_horaire || null} destination={destination} />

      <EmergencyBlock urgences={result?.urgences} destination={destination} compact={false} />

      <div className="card-surface">
        <h3 className="text-xl mb-3 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
          <BookOpen size={16} strokeWidth={1.5} className="text-[#C17F59]" /> Documents & formalités
        </h3>
        <InfoRow icon={<Globe size={14} strokeWidth={1.5} />} label="Visa" value={ip.visa} />
        <InfoRow icon={<CheckCircle size={14} strokeWidth={1.5} />} label="Passeport" value={ip.passeport} />
        <InfoRow icon={<Shield size={14} strokeWidth={1.5} />} label="Santé & vaccins" value={ip.sante_vaccins} />
      </div>

      {tripId && (
        <ScamRadarSection tripId={tripId} planType={planType} isAdmin={isAdmin} />
      )}

      <div className="card-surface">
        <h3 className="text-xl mb-3 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
          <MapPin size={16} strokeWidth={1.5} className="text-[#C17F59]" /> Sur place
        </h3>
        <InfoRow icon={<span className="text-sm">💱</span>} label="Monnaie" value={ip.monnaie_info} />
        <InfoRow icon={<span className="text-sm">🚌</span>} label="Transport local" value={ip.transport_local_detail} />
        <InfoRow icon={<Shield size={14} strokeWidth={1.5} />} label="Sécurité" value={ip.securite} />
        <InfoRow icon={<Zap size={14} strokeWidth={1.5} />} label="Électricité" value={ip.electricite} />
        <InfoRow icon={<Clock size={14} strokeWidth={1.5} />} label="Décalage horaire" value={ip.decalage_horaire} />
        <InfoRow icon={<Thermometer size={14} strokeWidth={1.5} />} label="Meilleure période" value={ip.meilleure_periode} />
      </div>

      {(ip.langue_principale || ip.mots_utiles?.length > 0) && (
        <div className="card-surface">
          <h3 className="text-xl mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
            Langue : {ip.langue_principale}
          </h3>
          {ip.mots_utiles?.map((phrase, i) => (
            <div key={i} className="flex items-start gap-2 text-sm py-1 border-b border-[#DCD4C6] last:border-0">
              <ChevronRight size={12} strokeWidth={2} className="text-[#C17F59] mt-0.5 flex-shrink-0" />
              <span className="text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>{phrase}</span>
            </div>
          ))}
        </div>
      )}

      {result?.meteo_par_mois?.length > 0 && (
        <div className="card-surface">
          <h3 className="text-xl mb-4 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
            <Thermometer size={16} strokeWidth={1.5} className="text-[#C17F59]" /> Météo par mois
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {result.meteo_par_mois.map((m, i) => (
              <div key={i} className="bg-[#F9F6F0] p-2.5 text-center border border-[#DCD4C6]" style={{ borderRadius: "1px" }} data-testid={`weather-month-${m.mois}`}>
                <p className="text-xs text-[#2B2623] mb-0.5" style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 500 }}>{m.mois}</p>
                <p className="text-lg mb-0.5">{weatherEmoji(m.description)}</p>
                <p className="text-xs text-[#C17F59]" style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 500 }}>{m.temp_max}°</p>
                <p className="text-xs text-[#9A8F86]" style={{ fontFamily: "'Lora', Georgia, serif" }}>{m.temp_min}°</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {result?.conseils_expert?.length > 0 && (
        <div className="card-surface">
          <h3 className="text-xl mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>Conseils d'expert</h3>
          <ul className="space-y-2">
            {result.conseils_expert.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <ChevronRight size={12} strokeWidth={2} className="text-[#C17F59] mt-0.5 flex-shrink-0" /> {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result?.erreurs_a_eviter?.length > 0 && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-sm">
          <h3 className="text-lg text-red-800 mb-3 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>
            <AlertTriangle size={15} strokeWidth={1.5} /> Erreurs à éviter
          </h3>
          <ul className="space-y-2">
            {result.erreurs_a_eviter.map((e, i) => (
              <li key={i} className="text-sm text-red-700 flex items-start gap-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <span className="flex-shrink-0">✗</span> {e}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result?.applications_utiles?.length > 0 && (
        <div className="card-surface">
          <h3 className="text-xl mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>Applications utiles</h3>
          <ul className="space-y-2">
            {result.applications_utiles.map((a, i) => (
              <li key={i} className="text-sm text-[#5C554E] flex items-start gap-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <Smartphone size={12} strokeWidth={1.5} className="text-[#C17F59] mt-0.5 flex-shrink-0" /> {a}
              </li>
            ))}
          </ul>
        </div>
      )}

      <OfflineMapCard carteHorsConnexion={result?.carte_hors_connexion} />
      <SecuritySection securite={result?.securite_locale} />
    </div>
  );
}
